import type { SiteMissingReports } from '@epc/contracts';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearch } from '@tanstack/react-router';
import { useCrumbs } from '../../layout/crumbs';
import { api } from '../../lib/api';
import { useMe } from '../../lib/session';
import { Badge, EmptyState, ErrorBox, Spinner } from '../../ui/feedback';
import { Page, PageHeader, Panel } from '../../ui/layout';
import { OnTimePanel } from './OnTimePanel';

type Search = { day?: string; project?: string };

const dateLabel = (date: string) =>
  new Date(`${date}T00:00:00Z`).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    weekday: 'short',
    timeZone: 'UTC',
  });

/** Список участков без сданного отчёта смены (ПР-05) и доля вовремя сданных за период (ПМ-05) — для РП и прораба. */
export function MissingReportsPage() {
  const me = useMe();
  const search = useSearch({ strict: false }) as Search;
  useCrumbs([{ label: 'Площадка', to: '/site' }, { label: 'Не сдали отчёт' }]);
  const q = useQuery({
    queryKey: ['site', me.tenant?.id, 'missing-reports', search.day],
    queryFn: () =>
      api<SiteMissingReports>('/site/missing-reports', {
        query: search.day ? { date: search.day } : {},
      }),
    enabled: Boolean(me.tenant?.staff),
  });
  if (!me.tenant?.staff)
    return (
      <Page>
        <ErrorBox error={new Error('Площадка доступна сотрудникам компании')} />
      </Page>
    );
  const items = (q.data?.items ?? []).filter((i) => !search.project || i.projectId === search.project);
  const hour = q.data ? `${String(q.data.hour).padStart(2, '0')}:00` : '…';
  return (
    <Page>
      <PageHeader
        title="Не сдали отчёт смены"
        subtitle={
          q.data
            ? `${dateLabel(q.data.date)} · срок ${hour} (${q.data.timezone})${
                q.data.pastDeadline ? ' — срок прошёл' : ' — ещё есть время'
              }`
            : 'Участки без отчёта на проверку'
        }
      />
      {q.isPending ? (
        <Spinner />
      ) : q.isError ? (
        <ErrorBox error={q.error} />
      ) : items.length === 0 ? (
        <EmptyState title="Все сдали">
          {search.project
            ? 'По выбранному проекту отчёты сданы или участков на день нет.'
            : 'На этот день нет выданных участков без сданного отчёта.'}
        </EmptyState>
      ) : (
        <Panel title={`Участков без отчёта: ${items.length}`}>
          <ul className="divide-y divide-line">
            {items.map((i) => (
              <li
                key={i.packageId}
                className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="min-w-0">
                  <div className="text-[15px] font-medium text-ink">
                    {i.projectCode}: {i.packageName}
                  </div>
                  <div className="text-[13px] text-ink-2">
                    {i.foremanName ? `Прораб: ${i.foremanName}` : 'Прораб не назначен'} · {i.projectName}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {q.data?.pastDeadline ? <Badge tone="danger">Просрочено</Badge> : <Badge>Ждёт сдачи</Badge>}
                  <Link
                    to="/site"
                    search={{ project: i.projectId, day: q.data?.date, package: i.packageId } as never}
                    className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-sm px-3 text-[14px] text-signal underline-offset-2 hover:underline"
                  >
                    К отчёту
                  </Link>
                </div>
              </li>
            ))}
          </ul>
        </Panel>
      )}
      <OnTimePanel projectId={search.project} />
    </Page>
  );
}
